import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const EASE = 'power2.out';

/*
 * Depuración (sin tocar el código):
 *   ?anim-debug  → marcadores de ScrollTrigger + logs en consola
 *   ?anim-force  → ignora "reducir movimiento" (solo para probar)
 */
const PARAMS = new URLSearchParams(window.location.search);
const DEBUG = PARAMS.has('anim-debug');
const FORCE = PARAMS.has('anim-force');
const log = (...args) => DEBUG && console.info('[anim]', ...args);

if (DEBUG) {
    window.gsap = gsap;
    window.ScrollTrigger = ScrollTrigger;
}

/* ---------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------ */

/**
 * Revela `targets` cuando `trigger` entra en pantalla (una sola vez).
 * `props` son los valores iniciales (desde donde parte la animación);
 * el resto de opciones (duration, delay, ease, stagger) van directo a GSAP.
 */
function reveal(targets, { trigger, start = 'top 85%', props = {}, ...timing } = {}) {
    const items = targets ? gsap.utils.toArray(targets) : [];

    if (!items.length) {
        log('sin elementos para', targets);
        return;
    }

    log(`reveal: ${items.length} elemento(s)`, targets);

    gsap.from(items, {
        opacity: 0,
        y: 24,
        duration: 0.7,
        ease: EASE,
        stagger: 0.12,
        clearProps: 'transform,opacity',
        ...props,
        ...timing,
        scrollTrigger: { trigger: trigger ?? items[0], start, once: true, markers: DEBUG },
    });
}

/**
 * Observa el atributo `hidden`: cuando un elemento deja de estar oculto
 * ejecuta su animación. Sirve para tabs y paneles sin tocar su JS.
 */
function watchHidden(root, handlers) {
    if (!root) return null;

    const observer = new MutationObserver((mutations) => {
        mutations.forEach(({ target }) => {
            if (target.hidden) return;

            Object.entries(handlers).forEach(([selector, animate]) => {
                if (target.matches(selector)) animate(target);
            });
        });
    });

    observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['hidden'] });

    return observer;
}

/**
 * Para secciones cuyo HTML no se conoce: bloques hijos del contenedor principal.
 * <section> > <div class="x__main"> > [bloques]
 */
function sectionBlocks(root) {
    const blocks = root.querySelectorAll(':scope > * > *');

    return blocks.length ? blocks : root.querySelectorAll(':scope > *');
}

/* ---------------------------------------------------------------------------
 * Secciones
 * ------------------------------------------------------------------------ */

function heroIntro() {
    const items = gsap.utils.toArray(
        '.page-hero__logo, .page-hero__heading, .page-hero__city, .page-hero__subsidy, .page-hero__description, .page-hero__body, .page-hero__actions'
    );

    if (!items.length) {
        log('hero: no se encontró .page-hero__logo / __heading / __description');
        return;
    }

    log(`hero: ${items.length} elemento(s)`);

    gsap.from(items, {
        opacity: 0,
        y: 28,
        duration: 0.9,
        ease: EASE,
        stagger: 0.15,
        delay: 0.2,
        clearProps: 'transform,opacity',
    });
}

function welcome() {
    if (!document.querySelector('.welcome')) return;

    // Intro: eyebrow, título, divisor y texto
    reveal('.welcome__intro > *', { trigger: '.welcome__intro' });

    // Imagen central y sello
    reveal('.welcome__media', {
        trigger: '.welcome__media',
        props: { y: 32, scale: 0.97 },
        duration: 0.9,
        stagger: 0,
    });
    reveal('.welcome__badge', {
        trigger: '.welcome__media',
        props: { y: 0, scale: 0.4 },
        ease: 'back.out(1.8)',
        duration: 0.6,
        delay: 0.35,
        stagger: 0,
    });

    // Estado del proyecto
    reveal('.welcome__status-title, .welcome__divider--status, .welcome__status-item', {
        trigger: '.welcome__status',
        stagger: 0.08,
    });

    // Atributos: ítems escalonados e íconos con "pop"
    reveal('.welcome__attributes-title', { trigger: '.welcome__attributes' });
    reveal('.welcome__attribute', {
        trigger: '.welcome__attributes-list',
        start: 'top 90%',
        stagger: 0.12,
    });
    reveal('.welcome__attribute-icon', {
        trigger: '.welcome__attributes-list',
        start: 'top 90%',
        props: { y: 0, opacity: 1, scale: 0.6 },
        ease: 'back.out(1.7)',
        duration: 0.5,
        delay: 0.2,
        stagger: 0.12,
    });
}

/**
 * Parallax del fondo. El CSS (banner-parallax-hero.scss) usa:
 *   --parallax-range → margen extra arriba/abajo de la capa (lo fija este JS)
 *   --parallax-y     → desplazamiento actual en px (lo anima este JS)
 * Intensidad por banner: data-parallax="0.3" (fracción de la altura; por defecto 0.2).
 */
function parallaxBackground(section, hero) {
    const base = parseFloat(section.dataset.parallax) || 0.2;
    const speed = window.innerWidth < 768 ? base * 0.6 : base; // más sutil en móvil

    hero.style.setProperty('--parallax-range', `${speed * 100 + 2}%`);

    gsap.fromTo(
        hero,
        { '--parallax-y': () => `${-hero.offsetHeight * speed}px` },
        {
            '--parallax-y': () => `${hero.offsetHeight * speed}px`,
            ease: 'none',
            scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
                markers: DEBUG,
            },
        }
    );
}

function banners() {
    gsap.utils.toArray('.banner-parallax').forEach((section) => {
        // Contenido (logo / título / descripción)
        reveal(section.querySelectorAll('.banner-parallax__content > *'), {
            trigger: section,
            start: 'top 75%',
        });

        const hero = section.querySelector('.banner-parallax__hero');
        if (hero) parallaxBackground(section, hero);
    });
}

/* Models: entrada de la sección (sidebar, imagen y bloque azul del panel visible) */
function modelsEntrance() {
    const root = document.querySelector('[data-js="models"]');
    if (!root) return;

    // Sidebar: título, tabs y flechas
    reveal(root.querySelectorAll('.models__header > *'), { trigger: '.models__sidebar', stagger: 0.1 });
    reveal(root.querySelectorAll('.models__btn, .models__arrow'), {
        trigger: '.models__sidebar',
        start: 'top 80%',
        props: { y: 16 },
        duration: 0.5,
        stagger: 0.07,
        delay: 0.15,
    });

    // Panel visible: imagen y bloque azul
    const panel = root.querySelector('.models__panel:not([hidden])');
    if (!panel) return;

    const image = panel.querySelector('.models__wrapper-image');
    const content = panel.querySelector('.models__model-wrapper-content');

    reveal(image, { trigger: image, props: { y: 32, scale: 0.98 }, duration: 0.9, stagger: 0 });
    reveal(panel.querySelectorAll('.models__model-name, .models__graphic, .models__actions, .models__specs'), {
        trigger: content,
        start: 'top 85%',
        stagger: 0.12,
    });
    reveal(panel.querySelector('.models__price'), {
        trigger: content,
        start: 'top 85%',
        props: { y: 0, scale: 0.9 },
        ease: 'back.out(1.6)',
        duration: 0.6,
        delay: 0.35,
        stagger: 0,
    });
}

/* Models: transición al cambiar de modelo o de fachada/piso */
function modelsTransitions(observers) {
    const root = document.querySelector('[data-js="models"]');

    observers.push(
        watchHidden(root, {
            // .models__panel usa display: contents, así que se animan sus hijos
            '.models__panel': (panel) =>
                gsap.fromTo(
                    panel.querySelectorAll('.models__wrapper-image, .models__model-wrapper-content'),
                    { opacity: 0, y: 16 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.45,
                        ease: EASE,
                        stagger: 0.08,
                        overwrite: true,
                        clearProps: 'transform,opacity',
                    }
                ),
            // Fachada / pisos
            '.models__media': (media) =>
                gsap.fromTo(
                    media,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.4, ease: 'power1.out', overwrite: true, clearProps: 'opacity' }
                ),
        })
    );
}

/*
 * Masterplan y tour virtual: no conozco su HTML, así que se animan los bloques
 * hijos del contenedor principal. Si no coinciden, revisa la consola con ?anim-debug.
 */
function masterplan() {
    const root = document.querySelector('#masterplan, .masterplan');

    if (!root) {
        log('masterplan: no se encontró #masterplan ni .masterplan');
        return;
    }

    reveal(sectionBlocks(root), { trigger: root, start: 'top 75%', stagger: 0.15 });

    // La imagen del masterplan crece suavemente (las <picture> de Imgv2)
    reveal(root.querySelectorAll('picture'), {
        trigger: root,
        start: 'top 70%',
        props: { y: 0, opacity: 1, scale: 0.96 },
        duration: 1,
        delay: 0.2,
        stagger: 0.1,
    });
}

function tourVirtual() {
    const root = document.querySelector('#virtual-tour, .virtual-tour');

    if (!root) {
        log('virtual-tour: no se encontró #virtual-tour ni .virtual-tour');
        return;
    }

    // Si es un banner-parallax, ya lo anima banners() (evita animar dos veces lo mismo)
    if (root.matches('.banner-parallax')) return;

    reveal(sectionBlocks(root), { trigger: root, start: 'top 75%', stagger: 0.15 });

    // Ícono 360: gira al aparecer
    reveal(root.querySelectorAll('img[src*="360"]'), {
        trigger: root,
        start: 'top 75%',
        props: { y: 0, opacity: 1, scale: 0.5, rotation: -90 },
        ease: 'back.out(1.7)',
        duration: 0.8,
        delay: 0.3,
        stagger: 0,
    });
}

function locationAnimations(observers) {
    if (!document.querySelector('.location')) return;

    reveal('.location__map', { trigger: '.location__main', props: { y: 30 }, duration: 0.9, stagger: 0 });
    reveal('.location__panel', {
        trigger: '.location__main',
        props: { y: 30 },
        duration: 0.9,
        delay: 0.15,
        stagger: 0,
    });

    // Tabs ejecutivos / horario
    observers.push(
        watchHidden(document.querySelector('[data-js="location-tabs"]'), {
            '.location__tabpanel': (panel) =>
                gsap.fromTo(
                    panel,
                    { opacity: 0, y: 10 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.35,
                        ease: EASE,
                        overwrite: true,
                        clearProps: 'transform,opacity',
                    }
                ),
        })
    );
}

/* ---------------------------------------------------------------------------
 * Declarativo: data-animate="fade-up | fade-in | zoom-in | slide-left | slide-right"
 *   data-animate-delay="0.2"        retraso en segundos
 *   data-animate-children           anima los hijos directos en cascada
 * ------------------------------------------------------------------------ */

const PRESETS = {
    'fade-up': { y: 24 },
    'fade-in': { y: 0 },
    'zoom-in': { y: 0, scale: 0.94 },
    'slide-left': { x: -40, y: 0 },
    'slide-right': { x: 40, y: 0 },
};

function declarative() {
    gsap.utils.toArray('[data-animate]').forEach((el) => {
        const props = PRESETS[el.dataset.animate] ?? PRESETS['fade-up'];
        const targets = el.hasAttribute('data-animate-children') ? Array.from(el.children) : [el];

        reveal(targets, {
            trigger: el,
            props,
            delay: parseFloat(el.dataset.animateDelay) || 0,
        });
    });
}

/* ---------------------------------------------------------------------------
 * Punto de entrada
 * ------------------------------------------------------------------------ */

export default function initAnimations() {
    const mm = gsap.matchMedia();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    log('GSAP', gsap.version, '| reducir movimiento activo:', reduced, FORCE ? '(ignorado por ?anim-force)' : '');

    if (reduced && !FORCE) {
        console.info(
            '[anim] Animaciones desactivadas: el sistema tiene "reducir movimiento" activo. ' +
            'Agrega ?anim-force a la URL para probarlas igualmente.'
        );
    }

    // Respeta "reducir movimiento": sin esta preferencia no se anima nada
    mm.add(FORCE ? '(min-width: 0px)' : '(prefers-reduced-motion: no-preference)', () => {
        const observers = [];

        heroIntro();
        welcome();
        banners();
        modelsEntrance();
        modelsTransitions(observers);
        masterplan();
        tourVirtual();
        locationAnimations(observers);
        declarative();

        log('ScrollTriggers creados:', ScrollTrigger.getAll().length);

        return () => observers.filter(Boolean).forEach((observer) => observer.disconnect());
    });

    // Las imágenes lazyload cambian la altura de la página: recalcula posiciones
    let timer;
    const refresh = () => {
        clearTimeout(timer);
        timer = setTimeout(() => ScrollTrigger.refresh(), 150);
    };

    window.addEventListener('load', refresh);
    document.addEventListener('lazyloaded', refresh);
}
